/**
 * Live pipeline timeline — shared by BOTH investigation flows.
 *
 * Originally this lived inside RecommendationInvestigationPanel. It moved here
 * when the anomaly RCA graph started emitting its own step events: two copies
 * of a live progress stepper is exactly how they drift apart, and the point of
 * this component is that a pipeline looks the same wherever you meet it.
 *
 * Everything rendered here corresponds to a real server-side step that
 * actually ran. Durations are computed from the server's own paired
 * started/completed timestamps — never a client-side timer standing in for
 * work that may not have happened. If a step isn't in the stream, it didn't
 * run, and nothing here invents one.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { getActiveTenantId } from '../../services/tenantContext';

const OPTIMIZATION_API_URL = import.meta.env.VITE_OPTIMIZATION_API_URL || 'http://localhost:3005';

/**
 * Human labels for every step either pipeline can emit.
 *
 * Top block: the recommendation approval orchestrator's safeStep calls.
 * Bottom block: the anomaly RCA LangGraph's seven nodes.
 *
 * A step the map doesn't recognise still renders, with its raw key — the panel
 * must never look more complete than what the backend actually reported.
 */
export const STEP_LABELS: Record<string, string> = {
  // --- Recommendation approval orchestrator
  classify_tier: 'Classify risk tier',
  generate_remediation: 'Generate remediation script/SOP',
  persist_remediation_artifact: 'Persist remediation artifact',
  l1_dry_run: 'Compute dry-run action',
  persist_approval_action: 'Save preview',
  mark_validating: 'Mark validating',
  l1_execute: 'Execute on cloud account',
  create_ticket: 'Create ticket',
  mark_final_status: 'Finalize status',

  // --- Single-shot RCA (rca-analysis.service.ts analyzeCostAnomaly).
  // Fewer steps than the graph because that path genuinely does less.
  retrieve_related: 'Fetch related recommendations',

  // --- Anomaly RCA graph (services/investigation/graph/nodes.ts)
  validate_anomaly: 'Check AI is permitted for this tenant',
  retrieve_similar: 'Retrieve similar past investigations',
  generate_hypotheses: 'Score deterministic candidate causes',
  collect_metrics: 'Collect utilization metrics',
  analyze_tags: 'Collect tagging-compliance context',
  detect_changes: 'Detect recent resource changes',
  synthesize: 'Synthesize and ground the narrative',
};

/**
 * Steps that the RCA graph dispatches in PARALLEL via LangGraph `Send()`.
 * Marked so the UI can say so, rather than showing three entries that merely
 * happen to look concurrent.
 */
export const PARALLEL_STEPS = new Set(['collect_metrics', 'analyze_tags', 'detect_changes']);

export interface StepEvent {
  step: string;
  status: 'started' | 'completed' | 'failed';
  detail?: string;
  timestamp: string;
}

/** One rendered row — built by pairing each step's own started and
 *  completed/failed events, both timestamped server-side. */
export interface StepTimelineEntry {
  step: string;
  status: 'running' | 'completed' | 'failed';
  startedAt: string | null;
  durationMs: number | null;
  detail?: string;
}

/**
 * Folds the raw event stream into one entry per step, in FIRST-SEEN order — so
 * the pipeline renders top-to-bottom in the order steps actually ran, not
 * alphabetically and not by last update.
 */
export function buildTimeline(events: StepEvent[]): StepTimelineEntry[] {
  const order: string[] = [];
  const byStep = new Map<string, { started?: StepEvent; ended?: StepEvent }>();
  for (const e of events) {
    if (!byStep.has(e.step)) {
      byStep.set(e.step, {});
      order.push(e.step);
    }
    const entry = byStep.get(e.step)!;
    if (e.status === 'started') entry.started = e;
    else entry.ended = e;
  }
  return order.map((step) => {
    const { started, ended } = byStep.get(step)!;
    const durationMs =
      started && ended
        ? Math.max(0, new Date(ended.timestamp).getTime() - new Date(started.timestamp).getTime())
        : null;
    return {
      step,
      status: ended ? (ended.status as 'completed' | 'failed') : 'running',
      startedAt: started?.timestamp || null,
      durationMs,
      detail: ended?.detail ?? started?.detail,
    };
  });
}

/**
 * Subscribes to the real websocket the backend emits on.
 *
 * `subjectId` is whatever is being investigated — a recommendation id for the
 * approval pipeline, an anomaly id for the RCA graph. The server rooms are
 * keyed by tenant + that id, so one hook serves both.
 *
 * Connect EAGERLY (when the tab opens), not on button click: establishing an
 * authenticated socket takes a JWT verify plus a tenant-authorization lookup,
 * which is slower than the first few pipeline steps take to run. Connecting
 * late meant the early events were already gone and the rest arrived in one
 * burst that looked fake.
 */
export function useInvestigationSocket(subjectId: string, enabled: boolean): StepTimelineEntry[] {
  const [events, setEvents] = useState<StepEvent[]>([]);

  useEffect(() => {
    if (!enabled) return;
    setEvents([]);
    const token = localStorage.getItem('accessToken');
    // Must go through getActiveTenantId(): it falls back to DEMO_TENANT_ID,
    // whereas reading localStorage directly yields undefined for anyone who
    // has never touched the Admin Console tenant switcher.
    const tenantId = getActiveTenantId();

    let socket: Socket | null = null;
    try {
      socket = io(OPTIMIZATION_API_URL, {
        path: '/ws/investigations',
        auth: { token, tenantId },
        transports: ['websocket', 'polling'],
        reconnectionAttempts: 3,
      });
      socket.on('connect', () => socket?.emit('subscribe', subjectId));
      socket.on('investigation:step', (event: StepEvent) => {
        setEvents((prev) => [...prev, event]);
      });
      socket.on('connect_error', () => {
        // Silent by design — the caller falls back to the persisted outcome.
        // A visible error for something as routine as "corporate proxy blocks
        // websockets" would be noise, not signal.
      });
    } catch {
      // socket.io throwing synchronously is as recoverable as a connect_error.
    }

    return () => {
      socket?.emit('unsubscribe', subjectId);
      socket?.disconnect();
    };
  }, [subjectId, enabled]);

  return useMemo(() => buildTimeline(events), [events]);
}

export function formatDuration(ms: number): string {
  return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;
}

export interface StepTimelineProps {
  steps: StepTimelineEntry[];
  /** Annotate the LangGraph `Send()` branches as genuinely concurrent. */
  showParallelHint?: boolean;
}

/**
 * Vertical connected stepper — icon nodes joined by a line, with each finished
 * step's real duration on the right.
 */
export const StepTimeline: React.FC<StepTimelineProps> = ({ steps, showParallelHint = false }) => {
  const parallelCount = showParallelHint ? steps.filter((s) => PARALLEL_STEPS.has(s.step)).length : 0;

  return (
    <div>
      {steps.map((s, i) => {
        const isLast = i === steps.length - 1;
        const isParallel = showParallelHint && PARALLEL_STEPS.has(s.step);
        const icon =
          s.status === 'completed' ? (
            <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
          ) : s.status === 'failed' ? (
            <XCircle className="h-5 w-5 text-rose-600 dark:text-rose-400" />
          ) : (
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
          );
        return (
          <div key={s.step} className="relative flex gap-3 pb-4 last:pb-0">
            {!isLast && <span className="absolute left-[9px] top-6 h-full w-px bg-secondary" />}
            <span className="z-10 shrink-0 rounded-full bg-card">{icon}</span>
            <div className="flex min-w-0 flex-1 items-baseline justify-between gap-2 pt-0.5">
              <div className="min-w-0">
                <span
                  className={`text-sm ${s.status === 'failed' ? 'text-rose-700 dark:text-rose-400' : 'text-foreground'}`}
                >
                  {STEP_LABELS[s.step] || s.step}
                </span>
                {isParallel && parallelCount > 1 && (
                  <span className="ml-2 rounded-full bg-primary/10 px-1.5 py-0.5 text-2xs font-medium text-primary">
                    parallel
                  </span>
                )}
                {s.detail && <div className="truncate text-xs text-muted-foreground">{s.detail}</div>}
              </div>
              {s.durationMs !== null && (
                <span className="shrink-0 text-xs tabular-nums text-muted-foreground/70">
                  {formatDuration(s.durationMs)}
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
